'use client';

import { api } from '~/trpc/react';
import { useAtom } from 'jotai';
import { useSearchParams } from 'next/navigation';
import { RouterOutputs } from '~/trpc/shared';
import { useTranslations } from 'next-intl';
import { useFilter } from '~/utils/useFilter';
import { addToCartDateSelectionAtom } from '~/atoms';

import Image from 'next/image';
import Link from 'next/link';

import { Card } from '~/components/ui/card';
import { Typography } from '~/components/ui/typography';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import AddToCart from './addToCart';

import { fCurrency } from '~/utils/formatNumber';
// @ts-ignore
import LinesEllipsis from 'react-lines-ellipsis';
import { calculateRentalPrice } from '~/utils/calculateRentalPrice';
import subDays from 'date-fns/subDays';
import { customEncodeURI } from '~/utils/encode-uri';

export default function Product(props: { product: RouterOutputs['product']['search'][number] }) {
  const t = useTranslations();
  const [filter] = useFilter();
  const searchParams = useSearchParams();
  const [_, setDateSelectionOpen] = useAtom(addToCartDateSelectionAtom);
  const datesAreSelected = !!filter?.dateRange?.from && !!filter?.dateRange?.to;
  let pricePerSelectedPeriod = {
    subTotal: 0,
    discount: 0,
    tax: 0,
    total: 0
  };
  let dayCount = !datesAreSelected
    ? 0
    : Math.ceil((filter.dateRange.to.getTime() - filter.dateRange.from.getTime()) / (1000 * 3600 * 24)) + 1;
  if (datesAreSelected) {
    pricePerSelectedPeriod = calculateRentalPrice(
      filter.dateRange,
      {
        dailyRentalPrice: props.product.product.dailyRentalPrice,
        weeklyRentalPrice: props.product.product.weeklyRentalPrice,
        monthlyRentalPrice: props.product.product.monthlyRentalPrice
      },
      props.product.merchant?.taxRate || 0
    );
  }

  return (
    <Card
      key={props.product.product.id}
      className="group shadow-none px-4 border-none hover:shadow-3xl transition-all cursor-pointer relative hover:z-20 before:absolute before:left-0 sm:before:opacity-0 before:bg-white before:content-[''] before:transition-all before:w-[calc(100%+0px)] before:h-[calc(100%+30px)] before:mt-[-15px] before:ml-[0px] hover:before:visible hover:before:opacity-100 before:shadow-3xl before:rounded-lg before:overflow-hidden before:opacity-100 mb-12 sm:mb-0"
    >
      <Link
        href={`/listing/${customEncodeURI(props.product.product.handle)}${
          !!searchParams.has('query') ? `/?query=${searchParams.get('query')}` : ''
        }`}
      >
        <div className="w-full aspect-square relative rounded-lg overflow-hidden mb-2">
          <Image
            alt={props.product.product.name}
            src={props.product.product.thumbnail}
            fill
            className='object-cover'
            sizes="(max-width: 768px) 100vw, 33vw"
          />
          {(props.product.merchant?.createdAt || new Date()) > subDays(new Date(), 30) && (
            <div className="absolute top-3 left-3 z-10">
              <Badge className="text-xs">{t('new')}</Badge>
            </div>
          )}
        </div>
      </Link>
      <div className="relative">
        <Link href={`/merchant/${customEncodeURI(props.product.merchant?.handle || '')}`}>
          <div className="flex flex-row gap-2 mt-4 mb-2">
            <div className="h-[30px] w-[30px] relative rounded-full shadow-lg overflow-hidden">
              <Image
                src={props.product.merchant?.thumbnail || ''}
                alt={props.product.merchant?.name || ''}
                fill
                objectFit="cover"
                sizes="(max-width: 50px) 100vw, 33vw"
              />
            </div>
            <div className="flex flex-col gap-0">
              <Typography variant="body" className="text-xs font-medium">
                {props.product.merchant?.name}
              </Typography>

              <Typography variant="body" className="text-xs text-gray-500">
                {props.product.merchant?.street}
              </Typography>
            </div>
          </div>
        </Link>
        <Link href={`/listing/${customEncodeURI(props.product.product.handle)}`}>
          <Typography variant="h3" className="text-sm font-medium">
            <LinesEllipsis text={props.product.product.name} maxLine="1" ellipsis="..." trimRight basedOn="letters" />
          </Typography>
          {datesAreSelected ? (
            <Typography variant="h3" className="text-sm font-light">
              {pricePerSelectedPeriod.total < pricePerSelectedPeriod.subTotal && (
                <span className="line-through pr-1 text-gray-400">{fCurrency(pricePerSelectedPeriod.subTotal)}€</span>
              )}
              {fCurrency(pricePerSelectedPeriod.total)}€ / {dayCount} {dayCount === 1 ? t('day') : t('days')}
            </Typography>
          ) : (
            <Typography variant="h3" className="text-sm font-light">
              {fCurrency(props.product.product.dailyRentalPrice)}€ / day
            </Typography>
          )}
        </Link>
        {datesAreSelected ? (
          <div className="opacity-100 sm:opacity-0 group-hover:opacity-100 mt-4">
            <AddToCart product={props.product} />
          </div>
        ) : (
          <Button
            onClick={() => {
              setDateSelectionOpen({ isOpen: true, product: props.product });
            }}
            className="w-full mt-4 opacity-100 sm:opacity-0 cursor-pointer group-hover:opacity-100"
          >
            {t('addToBooking')}
          </Button>
        )}
      </div>
    </Card>
  );
}
